import { useMemo, useRef } from "react";
import { cardToDeleteStore } from "features/CardToDelete";
import { filteredCardsStore } from "features/Filter";
import { LoadMoreButton } from "features/LoadMoreButton";
import { openedCardStore } from "features/OpenedCard";
import { useMainViewScrollRestoration } from "features/Panel/hooks/Panel.hooks";
import { useCardHandlers } from "features/Panel/hooks/Panel.hooks";
import { createSeaUpdateCardHook, useSeaCardActions } from "features/SeaCardActions";
import { useSeaSearch } from "features/Search";
import { usePanelViews } from "features/shared/usePanelViews.hook";
import { tagsStore, useTagsContext } from "features/Tags";
import { authStore } from "entities/Auth";
import { Card, ErrorCard, LoadCard } from "entities/Card";
import { PanelView } from "entities/PanelView";
import { searchFilterStore } from "entities/SearchFilter";
import { SEA_QUERY_CACHE_KEY, SeaSavedCard, seaSavedCardStore, useSeaInfiniteScrollFetch } from "entities/SeaSavedCard";
import { seaSearchCardStore } from "entities/SeaSearchCard";
import { SeaSearchCard } from "entities/SeaSearchCard/ui/SeaSearchCard";
import { mainViewId, savedShipmentLoadingText, searchShipmentLoadingText } from "shared/constants/panel.constants";
import { useAppDispatch, useAppSelector } from "shared/lib/redux";
import { useTranslation } from "shared/localization/localization";

type SeaViewProps = {
  className?: string;
};

export const SeaView: RFC<SeaViewProps> = ({ className }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const seaSearchCards = useAppSelector(seaSearchCardStore.selectors.searchCards);
  const seaSavedCards = useAppSelector(seaSavedCardStore.selectors.savedCards);
  const visibleSeaSearchCards = useMemo(() => {
    const savedReferences = new Set(seaSavedCards.map((card) => card.reference));
    return seaSearchCards.filter((card) => !card.reference || !savedReferences.has(card.reference));
  }, [seaSearchCards, seaSavedCards]);
  const isAuthenticated = useAppSelector(authStore.selectors.isAuthenticated);
  const openedCard = useAppSelector(openedCardStore.selectors.openedCard);
  const shipmentType = useAppSelector(searchFilterStore.selectors.shipment);
  const { showIntroView } = usePanelViews();
  const { refreshTags } = useTagsContext();
  const { isSeaSearchLoading } = useSeaSearch({ isAuthenticated });
  const { openCard } = useCardHandlers({
    openedCardAction: openedCardStore.actions.setOpenedCardAction,
  });

  useMainViewScrollRestoration(containerRef);

  const {
    deleteSeaCardHandler,
    seaCardFollow,
    isSeaCardFollowButtonVisible,
    isSeaCardFollowLoading,
    isSeaCardDeleting,
  } = useSeaCardActions({
    setCardToDeleteAction: (card) => ({
      type: cardToDeleteStore.actions.setCardToDeleteAction.type,
      payload: card,
    }),
    resetCardToDeleteAction: () => ({
      type: cardToDeleteStore.actions.resetCardToDeleteAction.type,
    }),
    removeFilteredCardAction: (card) => ({
      type: filteredCardsStore.actions.removeFilteredCardAction.type,
      payload: card,
    }),
    removeCardByTagAction: (card) => ({
      type: tagsStore.actions.removeCardByTagAction.type,
      payload: card,
    }),
    refreshTags,
  });

  const { requestSeaMoreCards, shouldRenderSeaInfiniteQueryLoader, shouldRenderSeaMoreButton } =
    useSeaInfiniteScrollFetch({
      containerRef,
      queryKey: [SEA_QUERY_CACHE_KEY],
      onSuccess: ({ page }) => {
        dispatch(seaSavedCardStore.actions.appendSavedCardsAction({ data: page }));
      },
      onSettled: ({ page }) => {
        if (!page?.length && !seaSearchCards.length) {
          showIntroView();
        }
      },
      shouldRequestOnMount: false,
    });

  return (
    <PanelView
      ref={containerRef}
      className={className}
      id={mainViewId}
      shouldRenderMoreButton={shouldRenderSeaMoreButton}
      loadMoreButton={<LoadMoreButton onClick={requestSeaMoreCards} />}
    >
      {isSeaSearchLoading && <LoadCard text={t(searchShipmentLoadingText)} />}

      {visibleSeaSearchCards.map((card, index) => (
        <SeaSearchCard
          key={card.reference ?? index}
          data={card}
          isAuthenticated={isAuthenticated}
          openCard={openCard}
          deleteCardHandler={deleteSeaCardHandler}
          follow={seaCardFollow}
          isFollowButtonVisible={isSeaCardFollowButtonVisible}
          isFollowLoading={isSeaCardFollowLoading}
          shipmentType={shipmentType}
          renderCard={(props) => <Card {...props} />}
          renderErrorCard={(props) => <ErrorCard {...props} />}
        />
      ))}

      {isAuthenticated && (
        <>
          {seaSavedCards.map((card) => (
            <SeaSavedCard
              key={card.reference}
              data={card}
              openCard={openCard}
              isSavedCardDeleting={isSeaCardDeleting}
              useSavedCardUpdateActions={createSeaUpdateCardHook({
                model: card,
                isOpenedCardExist: !!openedCard,
                openedCardUpdateAction: (response) => ({
                  type: openedCardStore.actions.updateOpenCardAction.type,
                  payload: response,
                }),
                updateFilteredCards: (response) => ({
                  type: filteredCardsStore.actions.updateFilterCardAction.type,
                  payload: response,
                }),
                updateCardByTagAction: (response) => ({
                  type: tagsStore.actions.updateCardByTagAction.type,
                  payload: response,
                }),
              })}
              deleteCardHandler={deleteSeaCardHandler}
              shipmentType={shipmentType}
              renderCard={(props) => <Card {...props} />}
            />
          ))}
          {shouldRenderSeaInfiniteQueryLoader && <LoadCard text={t(savedShipmentLoadingText)} />}
        </>
      )}
    </PanelView>
  );
};
